import { DomUtils, ElementType, parseDocument } from 'htmlparser2'

export type HtmlDocument = ReturnType<typeof parseDocument>
export type HtmlNode = HtmlDocument['children'][number]
export type HtmlElement = Extract<
  NonNullable<ReturnType<typeof DomUtils.findOne>>,
  { attribs: Record<string, string>; children: HtmlNode[]; name: string }
>

export function parseHtml(body: string, decodeEntities = true): HtmlDocument {
  return parseDocument(body, {
    decodeEntities,
  })
}

export function serializeHtml(
  node: HtmlDocument | HtmlNode,
  decodeEntities = true,
): string {
  return DomUtils.getOuterHTML(
    node,
    decodeEntities ? { encodeEntities: 'utf8' } : { decodeEntities: false },
  )
}

export function isHtmlElement(node: HtmlNode): node is HtmlElement {
  return (
    node.type === ElementType.Tag ||
    node.type === ElementType.Script ||
    node.type === ElementType.Style
  )
}

export function isHtmlScript(node: HtmlNode): boolean {
  return (
    node.type === ElementType.Script ||
    (isHtmlElement(node) && node.name.toLowerCase() === 'script')
  )
}

export function htmlHasScript(nodes: HtmlNode[]): boolean {
  return DomUtils.findOne(isHtmlScript, nodes) !== null
}

export function removeHtmlScripts(nodes: HtmlNode[]): void {
  for (const node of DomUtils.findAll(isHtmlScript, nodes))
    DomUtils.removeElement(node)
}

export function isHtmlText(
  node: HtmlNode,
): node is HtmlNode & { data: string } {
  return node.type === ElementType.Text
}

export function findHtmlElements(
  nodes: HtmlNode[],
  predicate: (element: HtmlElement) => boolean = () => true,
): HtmlElement[] {
  return DomUtils.findAll(
    (node): node is HtmlElement => isHtmlElement(node) && predicate(node),
    nodes,
  ) as HtmlElement[]
}

export function htmlElementIsInside(
  element: HtmlElement,
  ancestorName: string,
): boolean {
  let node: HtmlNode | null = element
  while (node) {
    if (isHtmlElement(node) && node.name.toLowerCase() === ancestorName)
      return true
    node = node.parent as HtmlNode | null
  }
  return false
}

export function removeHtmlNode(node: HtmlNode): void {
  DomUtils.removeElement(node)
}

export function cloneHtmlElement(element: HtmlElement): HtmlElement {
  const copy = findHtmlElements(parseHtml(serializeHtml(element)).children)[0]
  if (!copy) throw new Error('Could not clone HTML element.')
  return copy
}

export function createHtmlText(text: string): HtmlNode & { data: string } {
  const node = parseHtml('x').children[0]
  if (!node || !isHtmlText(node)) throw new Error('Could not create HTML text.')
  node.data = text
  return node
}

export function htmlTextContent(body: string): string {
  return DomUtils.textContent(parseHtml(body))
}
